'use client';

import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Resource, Category } from '@/data/resources';
import { useTranslation } from '@/hooks/useTranslation';

interface SearchProps {
  onSearch: (results: Resource[]) => void;
  categories: Category[];
}

const Search = ({ onSearch, categories }: SearchProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { t, language } = useTranslation();

  // 创建带有翻译信息的资源列表，只在语言变化或组件挂载时重新计算
  const allResources = useMemo(() => {
    return categories.flatMap(category => 
      category.resources.map(resource => ({
        ...resource,
        category: t(category.id),
        translatedDescription: t(`desc-${resource.id}`) !== `desc-${resource.id}` 
          ? t(`desc-${resource.id}`) 
          : resource.description
      }))
    );
  }, [t, categories]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (!term.trim()) {
      onSearch([]);
      return;
    }

    // Filter resources based on search term, now including translated descriptions
    const searchTermLower = term.toLowerCase();
    const results = allResources.filter(resource => 
      resource.name.toLowerCase().includes(searchTermLower) || 
      resource.translatedDescription.toLowerCase().includes(searchTermLower) ||
      resource.category.toLowerCase().includes(searchTermLower)
    );

    onSearch(results);
  };

  return (
    <div className="relative mb-8">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out text-sm"
          placeholder={t('searchPlaceholder')}
        />
      </div>
    </div>
  );
};

export default Search; 